import { useState, useEffect } from "react"
import { useSaleCreateStore } from "../../stores/useSaleCreateStore"
import { TimberProduct } from "../../types"

interface ProductRow extends TimberProduct {
  id: string
}

export const TimberProductsStep = () => {
  const { saleData, updateTimberProducts, updateInventoryDescription } =
    useSaleCreateStore()

  const COMMON_UNITS = ["ton", "MBF", "cord", "CCF", "board feet"]
  const INITIAL_PRODUCTS = [
    "Pine Saw",
    "Pine CNS",
    "Pine Pulp",
    "HW Saw",
    "HW Pulp",
  ]

  // Initialize rows with common products or existing data
  const initializeRows = (): ProductRow[] => {
    if (saleData.timber_products && saleData.timber_products.length > 0) {
      return saleData.timber_products.map((product, index) => ({
        ...product,
        id: `product-${index}`,
      }))
    }

    return INITIAL_PRODUCTS.map((name, index) => ({
      id: `product-${index}`,
      name,
      unit: "ton",
      estimated_volume: 0,
    }))
  }

  const [productRows, setProductRows] = useState<ProductRow[]>(initializeRows())

  // Update store whenever products change
  useEffect(() => {
    const validProducts = productRows
      .filter((row) => row.name.trim())
      .map(({ id: _id, ...product }) => product) // Remove id for store
    updateTimberProducts(validProducts)
  }, [productRows, updateTimberProducts])

  const updateRow = (
    id: string,
    field: keyof TimberProduct,
    value: string | number
  ) => {
    setProductRows((rows) =>
      rows.map((row) => (row.id === id ? { ...row, [field]: value } : row))
    )
  }

  const addRow = () => {
    const newId = `product-${Date.now()}`
    setProductRows((rows) => [
      ...rows,
      {
        id: newId,
        name: "",
        unit: "ton",
        estimated_volume: 0,
      },
    ])
  }

  const removeRow = (id: string) => {
    setProductRows((rows) => rows.filter((row) => row.id !== id))
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          Timber Products Inventory
        </h3>
        <p className="text-gray-600 mb-4">
          Enter the estimated volume for each timber product that will be
          included in this sale. You can delete any rows for products you don't
          have.
        </p>
      </div>

      {/* Inventory Description */}
      <div className="bg-white rounded-lg border p-4">
        <label
          htmlFor="inventory-description"
          className="block text-sm font-medium text-gray-700 mb-2"
        >
          Inventory Description (Optional)
        </label>
        <p className="text-sm text-gray-500 mb-3">
          Provide additional details about the timber inventory, site
          conditions, access, or other relevant information for bidders.
        </p>
        <textarea
          id="inventory-description"
          value={saleData.inventory_description || ""}
          onChange={(e) => updateInventoryDescription(e.target.value)}
          rows={4}
          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
          placeholder="Example: Approximately 150 mature oak trees ready for selective harvest. Site has excellent road access with minimal environmental restrictions. Harvesting should be completed during dry season to minimize soil compaction."
        />
      </div>

      {/* Unified Products Form */}
      <div className="bg-white rounded-lg border overflow-hidden">
        <div className="px-4 py-3 bg-gray-50 border-b">
          <h4 className="text-sm font-medium text-gray-900">Timber Products</h4>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Product Name
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Estimated Volume
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Unit
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {productRows.map((row) => (
                <tr key={row.id}>
                  <td className="px-4 py-3">
                    <input
                      type="text"
                      value={row.name}
                      onChange={(e) =>
                        updateRow(row.id, "name", e.target.value)
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Product name"
                    />
                  </td>
                  <td className="px-4 py-3">
                    <input
                      type="number"
                      value={row.estimated_volume || ""}
                      onChange={(e) =>
                        updateRow(
                          row.id,
                          "estimated_volume",
                          parseFloat(e.target.value) || 0
                        )
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                      placeholder="0"
                      min="0"
                      step="0.1"
                    />
                  </td>
                  <td className="px-4 py-3">
                    <select
                      value={row.unit}
                      onChange={(e) =>
                        updateRow(row.id, "unit", e.target.value)
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                    >
                      {COMMON_UNITS.map((unit) => (
                        <option key={unit} value={unit}>
                          {unit}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => removeRow(row.id)}
                      className="text-red-600 hover:text-red-800 text-sm"
                      title="Remove this product"
                    >
                      <svg
                        className="h-4 w-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                        />
                      </svg>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="px-4 py-3 bg-gray-50 border-t">
          <button
            onClick={addRow}
            className="flex items-center text-sm text-blue-600 hover:text-blue-800"
          >
            <svg
              className="h-4 w-4 mr-1"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4v16m8-8H4"
              />
            </svg>
            Add Another Product
          </button>
        </div>
      </div>
    </div>
  )
}
